import apis, { itemUpdate } from '@/apis/requirement';
import quoApis from '@/apis/quotationRequestItem';
import type { QuotationPurchaseRound } from '@/apis/quotationRequestItem';
import useDataList from '@/hooks/useDataList';
import { QuotationRequestItem } from '@/types/quotationRequestItem';
import { dateFormat } from '@/utils/format';
import { Modal, Button, Space, App, Tag, Flex, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Org } from '@/types/org';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { t } from 'i18next';
import { PurchaseOrderItem, PurchaseOrderStateEnum } from '@/types/purchase';
import { QuotationRequest, RequirementItem, RequirementItemDelEnum } from '@/types/requirement';
import useRequirementParams from '@/pages/Requirement/hooks/useRequirementParams';
import { PageId } from '@/types/page';
import QuotationForm from '@/components/QuotationForm';
import SelctedQuoPrint from './SelctedQuoPrint';
import { Product } from '@/types/product';
import { getQuotationPrice, numFormmater } from '@/pages/Requirement/util';
import CommonTable from "@/components/CommonTable";
import SText from '@/styles/SText';


interface PurchaseModalProps {
  open: boolean;
  onOk: (data: QuotationRequest[]) => void;
  onCancel: () => void;
  id: string;
  itemId?: string;
  reqNumber: string;
}

export const PurchaseModal = ({
  open,
  onOk,
  onCancel,
  id,
  itemId = '',
  reqNumber,
}: PurchaseModalProps) => {
  const currentOrg: Org = useSelector((state: RootState) => state.orgReducer.curOrg);
  const { notification, modal } = App.useApp()
  const [list, , listLoading, { fetchList }] = useDataList<QuotationRequestItem, [string]>({ fetchApi: apis.selectedQuotationRequest });
  const [dataList, setDataList] = useState<QuotationRequestItem[]>([])

  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const { pageId } = useRequirementParams(true);
  const [quoDetail, setQuoDetail] = useState<QuotationRequestItem | null>(null)


  const columns: ColumnsType<QuotationRequestItem> = [
    {
      title: t("purchase.name"),
      dataIndex: ['supplier', 'name'],
      key: 'supplierName',
      width: 100,
      render(value, record, index) {
        return <SText text={value ?? ''} />
      },
    },
    {
      title: t("purchase.code"),
      dataIndex: ['supplier', 'code'],
      key: 'supplierCode',
      width: 100,
      render(value, record, index) {
        return <SText text={value ?? ''} />
      }
    },
    {
      title: t("purchase.materialCode"),
      dataIndex: ['requirementItem', 'product', 'code'],
      key: 'productCode',
      width: 120,
      render(value, record, index) {
        return <SText text={value ?? ''} />
      },
    },
    {
      title: t("purchase.materialName"),
      dataIndex: ['requirementItem', 'product', 'name'],
      key: 'productName',
      width: 140,
      render(value, record, index) {
        return <SText text={value ?? ''} />
      },
    },
    {
      title: t("purchase.materialModel"),
      dataIndex: ['requirementItem', 'product', 'model'],
      key: 'productModel',
      width: 160,
      render(value, record, index) {
        return <SText text={record?.requirementItem?.product?.model ?? ''} />
      },
    },
    {
      title: t("purchase.purCount"),
      dataIndex: 'count',
      key: 'count',
      width: 100,
    },
    {
      title: t("purchase.purPrice"),
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (_, record) => {
        const price = getQuotationPrice(record);
        return price === null ? '' : numFormmater(price);
      }
    },
    {
      title: t("purchase.orderState"),
      dataIndex: 'count',
      key: 'orderState',
      width: 120,
      render: (_, record) => (
        record.purchaseItem ?
          record.purchaseItem.orderState === PurchaseOrderStateEnum.PAYED ? <Tag color='success'>{t('purchase.purchased')}</Tag>
            : record.purchaseItem.orderState === PurchaseOrderStateEnum.DONE ? <Tag color='success'>{t('purchase.purchased')}</Tag>
              : <Tag color='blue'>{t('purchase.notPurchase')}</Tag>
          : <Tag color='warning'>{t('purchase.noPurOrder')}</Tag>
      )
    },
    {
      title: t("purchase.supplierTime"),
      dataIndex: 'supplierTime',
      key: 'supplierTime',
      width: 100,
      render: (text: string) => dateFormat(dayjs(Number(text)), false),
    },
    {
      title: t("purchase.mpq"),
      dataIndex: 'mpq',
      width: 100,
      key: 'mpq',
    },
    {
      title: t("purchase.days"),
      dataIndex: 'days',
      key: 'days',
      width: 100,
    },
    {
      title: t("common.opt"),
      width: 120,
      fixed: "right",
      render: (_, record) => <Button type="link" onClick={() => setQuoDetail(record)}>{t('common.detail')}</Button>
    }
  ];

  useEffect(() => {
    if (open && id) {
      fetchList(id)
    }
  }, [open, id])

  useEffect(() => {
    if (itemId) setDataList(list.filter(item => item.requirementItemId === itemId && item?.requirementItem?.deleted !== RequirementItemDelEnum.DEL))
    else setDataList([...list].filter(item => item?.requirementItem?.deleted !== RequirementItemDelEnum.DEL))
  }, [list, itemId])

  const handlePurchese = () => {
    let selectedRound: QuotationPurchaseRound = 0;

    modal.confirm({
      title: t("purchase.addPurOrder"),
      content: (
        <Flex vertical gap={8}>
          <span>{t("purchase.addPurOrderTip")}</span>
          <span>{t("purchase.roundMethod")}</span>
          <Select<QuotationPurchaseRound>
            defaultValue={selectedRound}
            options={[
              { label: t("purchase.roundNone"), value: 0 },
              { label: t("purchase.roundDown"), value: 1 },
              { label: t("purchase.roundUp"), value: 2 },
              { label: t("purchase.roundNearest"), value: 3 },
            ]}
            onChange={value => {
              selectedRound = value;
            }}
          />
        </Flex>
      ),
      onOk() {
        return handleOk(selectedRound)
      },
    });
  }

  const handleOk = (round: QuotationPurchaseRound) => {
    setPurchaseLoading(true)
    return quoApis.quotationPurchase(dataList.map(item => item.id), currentOrg?.id || '', round)
      .then(async (res) => {
        if (res && res.length > 0) {
          // let updateItem: Array<Promise<RequirementItem>> = []
          // res.forEach(r => {
          //   r.requirementItem && r.purchaseItem && updateItem.push(
          //     itemUpdate(r.requirementItemId, {
          //       ...r.requirementItem,
          //       puredCount: r.purchaseItem.count + '',
          //     })
          //   )
          // })
          // await Promise.allSettled(updateItem)
          notification.success({ placement: 'top', message: t("purchase.addPurSuccessTip"), description: t("purchase.addPurSuccessTipDesc", { all: list.length, suc: res.length }) })
        }
        else notification.success({ placement: 'top', message: t("purchase.addNoPurSuccess") })
        onOk(res)
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setPurchaseLoading(false)
      })
  }

  const purOrders: Array<PurchaseOrderItem> = useMemo(() => {
    let purOrders: Array<PurchaseOrderItem> = [];
    if (dataList && dataList?.length > 0) {
      dataList.forEach(data => {
        purOrders.push({
          ...data.purchaseItem,
          product: { ...data.requirementItem.product } as Product
        })

      })
    }
    return purOrders;
  }, [dataList])

  return (
    <Modal
      title={
        <Flex style={{ width: "100%", boxSizing: "border-box", paddingRight: 34 }} justify="space-between" >
          <span>{itemId ? t("requirement.prePurchaseOrders") : t("purchase.purConfirm")}</span>
          <SelctedQuoPrint reqNumber={reqNumber} datas={purOrders} />
        </Flex>
      }
      open={open}
      width={1000}
      onCancel={onCancel}
      footer={
        !itemId || (itemId && pageId === PageId.PUR_ORDER_PAYED) ? <Space>
          <Button onClick={onCancel} disabled={purchaseLoading}>{t("purchase.notPur")}</Button>
          <Button type="primary" onClick={handlePurchese} loading={purchaseLoading} disabled={dataList.length === 0}>
            {t("purchase.conPur")}
          </Button>
        </Space>
          : null
      }
    >
      <CommonTable
        rowKey="id"
        columns={columns}
        dataSource={dataList}
        virtual
        pagination={false}
        bordered={false}
        scroll={{ x: 800, y: 400 }}
        loading={listLoading}
      />
      <QuotationForm formKey="detail" detailId={quoDetail?.id || ''} onCancel={() => setQuoDetail(null)} onSubmit={() => { }} onReject={() => { }} />
    </Modal>
  );
};
